<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue';

// PROPS
const props = defineProps<{
  gtmId: string;
  gtmEnabled: boolean;
  cookiesEnabled: boolean;
}>();

const scriptId = 'gtm-script';
const noScriptId = 'gtm-noscript';

// UTILS
const loadGtmScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if ((window as any).gtmDidLoad) {
      resolve();
      return;
    }

    // Initialize dataLayer if needed
    (window as any).dataLayer = (window as any).dataLayer || [];

    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${props.gtmId}`;
      script.onload = () => {
        (window as any).gtmDidLoad = true;
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);

      const noscript = document.createElement('noscript');
      noscript.id = noScriptId;
      noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${props.gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.appendChild(noscript);
    } else {
      const checkInterval = setInterval(() => {
        if ((window as any).gtmDidLoad) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkInterval);
        reject(new Error('GTM script load timeout'));
      }, 5000);
    }
  });
};

const removeGtmScript = () => {
  document.getElementById(scriptId)?.remove();
  document.getElementById(noScriptId)?.remove();
  delete (window as any).gtmDidLoad;
};

const pushDefaultConsent = () => {
  (window as any).dataLayer.push({
    event: 'default_consent',
    defaultAdsConsent: 'denied',
    defaultAnalyticsConsent: 'denied',
  });
};

const pushCookiesEnabled = () => {
  (window as any).dataLayer.push({
    event: 'update_consent',
    analytics_storage: 'granted',
    ads_storage: 'granted',
  });
};

// LOGIC
const setupGtm = async () => {
  try {
    await loadGtmScript();
    pushDefaultConsent();
    if (props.cookiesEnabled) {
      pushCookiesEnabled();
    }
  } catch (error) {
    console.error('Failed to load GTM', error);
  }
};

// LIFECYCLE
onMounted(() => {
  if (props.gtmEnabled) {
    setupGtm();
  }
});

onUnmounted(() => {
  removeGtmScript();
});

// WATCHERS
watch(
  () => props.gtmEnabled,
  (enabled) => {
    if (enabled) {
      setupGtm();
    } else {
      removeGtmScript();
    }
  },
);

watch(
  () => props.cookiesEnabled,
  (cookies) => {
    if (cookies && props.gtmEnabled && (window as any).gtmDidLoad) {
      pushCookiesEnabled();
    }
  },
);
</script>

<template>
  <div></div>
</template>
